// src/context/RiderContext.js
import React, {
  createContext, useContext, useState, useEffect, useRef, useCallback,
} from 'react';
import { auth, clearRiderLocation, updateRiderLocation, listenToRiderActiveOrder, listenToRiderRating } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';

const ACTIVE_DELIVERY_STATUSES = ['confirmed', 'ready', 'picked_up'];

const RiderContext = createContext(null);

export const RiderProvider = ({ children }) => {
  const [rider, setRider] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(false);
  const [currentRide, setCurrentRide] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [riderRating, setRiderRating] = useState(null);
  const [jobStats, setJobStats] = useState({ todayTrips: 0, totalTrips: 0, jobs: [] });

  const coordsRef = useRef({ lat: null, lng: null });
  const locationWatchRef = useRef(null);
  const locationWarnedRef = useRef(false);

  const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 20000, maximumAge: 10000 };

  const getRiderCoords = useCallback(() => {
    const { lat, lng } = coordsRef.current;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return {};
    if (Math.abs(lat) < 0.01 && Math.abs(lng) < 0.01) return {};
    return { riderLat: lat, riderLng: lng };
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setRider(user);
      setAuthLoading(false);
      if (!user) {
        setIsOnline(false);
        setActiveOrder(null);
        setCurrentOrder(null);
        setRiderRating(null);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!rider?.uid) return undefined;
    const unsubActive = listenToRiderActiveOrder(rider.uid, (order) => {
      setActiveOrder(order);
      if (order) {
        setCurrentOrder((prev) => (prev?.id === order.id ? { ...prev, ...order } : order));
      } else {
        setCurrentOrder((prev) => {
          if (prev && ACTIVE_DELIVERY_STATUSES.includes(prev.status)) return null;
          return prev;
        });
      }
    });
    const unsubRating = listenToRiderRating(rider.uid, setRiderRating);
    return () => {
      unsubActive();
      unsubRating();
    };
  }, [rider?.uid]);

  const primeLocation = useCallback(() => new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ ok: false, reason: 'unsupported' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          resolve({ ok: false, reason: 'invalid' });
          return;
        }
        coordsRef.current = { lat, lng };
        if (rider?.uid) updateRiderLocation(rider.uid, lat, lng);
        locationWarnedRef.current = false;
        resolve({ ok: true, lat, lng });
      },
      (err) => resolve({ ok: false, reason: err?.code ?? 'unknown' }),
      { ...GEO_OPTIONS, maximumAge: 0 },
    );
  }), [rider?.uid]);

  useEffect(() => {
    if (!isOnline || !rider?.uid) {
      if (locationWatchRef.current != null) {
        navigator.geolocation?.clearWatch(locationWatchRef.current);
        locationWatchRef.current = null;
      }
      locationWarnedRef.current = false;
      if (rider?.uid) clearRiderLocation(rider.uid);
      return undefined;
    }

    const onPos = (pos) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      if (Math.abs(lat) < 0.01 && Math.abs(lng) < 0.01) return;
      coordsRef.current = { lat, lng };
      locationWarnedRef.current = false;
      updateRiderLocation(rider.uid, lat, lng);
    };

    if (!navigator.geolocation) return undefined;

    locationWatchRef.current = navigator.geolocation.watchPosition(onPos, () => {}, GEO_OPTIONS);

    return () => {
      if (locationWatchRef.current != null) {
        navigator.geolocation.clearWatch(locationWatchRef.current);
        locationWatchRef.current = null;
      }
    };
  }, [isOnline, rider?.uid]);

  const locationIssueKey = useCallback((reason) => {
    if (reason === 1 || reason === 'denied' || reason === 'permission-denied') return 'locationDenied';
    return 'locationWeak';
  }, []);

  return (
    <RiderContext.Provider value={{
      rider, setRider, authLoading,
      isOnline, setIsOnline,
      currentRide, setCurrentRide,
      currentOrder, setCurrentOrder,
      activeOrder,
      riderRating,
      jobStats, setJobStats,
      getRiderCoords,
      primeLocation,
      locationIssueKey,
    }}>
      {children}
    </RiderContext.Provider>
  );
};

export const useRider = () => useContext(RiderContext);
